import type {
  IncomeReportDeliveryType,
  IncomeReportGenerateResponse,
  IncomeReportStatusResponse,
  IncomeReportAvailableYearsResponse,
} from '@/types/incomeReport';
import { useIncomeReportStore } from '@/stores/incomeReport';

const POLLING_INTERVAL = 3000;
const MAX_POLLING_ATTEMPTS = 40;

export const useIncomeReport = () => {
  const { t } = useTexts();
  const { ApiService } = useApiService();

  const {
    currentReport,
    isGenerating,
    isPolling,
    pollingIntervalId,
    error,
    allowedTypes,
  } = storeToRefs(useIncomeReportStore());

  const {
    setCurrentReport,
    setGenerating,
    setPolling,
    setPollingIntervalId,
    setError,
    setAllowedTypes,
  } = useIncomeReportStore();

  let pollingAttempts = 0;

  const availableYears = ref<number[]>([]);

  async function generateIncomeReport(
    year: number,
    type: IncomeReportDeliveryType,
  ) {
    setGenerating(true);
    setError(null);

    try {
      const { data } = await ApiService.post<IncomeReportGenerateResponse>(
        '/income-report/generate',
        {
          reference_year: year,
          type,
        },
      );

      if (data && data.data) {
        setCurrentReport(data.data);

        if (type === 'download') {
          startPolling(data.data.id);
        }

        return { success: true, data: data.data };
      }

      return {
        success: false,
        error: t('income_report.errors.invalid_response'),
      };
    } catch (err: any) {
      console.error('generateIncomeReport error:', err);

      if (err?.status === 422) {
        if (err?.data?.data?.allowed_types) {
          setAllowedTypes(err.data.data.allowed_types);
          setError(t('income_report.errors.type_not_allowed'));

          return {
            success: false,
            error: 'type_not_allowed',
            allowedTypes: err.data.data.allowed_types,
          };
        }

        if (err?.data?.data?.id) {
          setCurrentReport(err.data.data);
          setError(t('income_report.errors.already_processing'));

          return {
            success: false,
            error: 'already_processing',
            existingReport: err.data.data,
          };
        }

        if (err?.data?.errors) {
          const errorMessages = Object.values(err?.data?.errors ?? {})
            .flat()
            .join(', ');

          setError(errorMessages);

          return {
            success: false,
            error: 'validation_error',
            message: errorMessages,
          };
        }

        if (err?.data?.message) {
          setError(t('income_report.no_income_report_available_message'));

          return {
            success: false,
            error: 'no_income_report_available',
          };
        }

        setError(t('income_report.no_income_report_available_message'));

        return {
          success: false,
          error: 'no_income_report_available',
        };
      }

      setError(t('income_report.errors.generate_error'));

      return { success: false, error: 'unknown_error' };
    } finally {
      setGenerating(false);
    }
  }

  async function checkReportStatus(id: number) {
    try {
      const { data } = await ApiService.get<IncomeReportStatusResponse>(
        `/income-report/${id}`,
      );

      if (data && data.data) {
        setCurrentReport(data.data);

        return data.data;
      }

      return null;
    } catch (err: any) {
      console.error('checkReportStatus error:', err);

      if (err?.status === 404) {
        setError(t('income_report.errors.not_found'));
      }

      return null;
    }
  }

  function startPolling(id: number) {
    if (pollingIntervalId.value) {
      stopPolling();
    }

    setPolling(true);
    pollingAttempts = 0;

    const intervalId = setInterval(async () => {
      pollingAttempts++;

      if (pollingAttempts >= MAX_POLLING_ATTEMPTS) {
        stopPolling();
        setError(t('income_report.errors.polling_timeout'));

        return;
      }

      const report = await checkReportStatus(id);

      if (report && report.status === 3) {
        stopPolling();
      }

      if (report && report.status === 4) {
        stopPolling();
        setError(t('income_report.errors.processing_failed'));
      }
    }, POLLING_INTERVAL);

    setPollingIntervalId(intervalId);
  }

  function stopPolling() {
    if (pollingIntervalId.value) {
      clearInterval(pollingIntervalId.value);
      setPollingIntervalId(null);
    }

    setPolling(false);
    pollingAttempts = 0;
  }

  async function fetchAvailableYears() {
    try {
      const { data } = await ApiService.get<IncomeReportAvailableYearsResponse>(
        '/income-report/available-years',
      );

      if (data && data.data && data.data.years) {
        availableYears.value = data.data.years;

        return { success: true, years: data.data.years };
      }

      return { success: false, error: 'invalid_response' };
    } catch (err: any) {
      console.error('fetchAvailableYears error:', err);

      if (err?.status === 403 && err?.data?.message) {
        return {
          success: false,
          error: 'forbidden',
          message: t('income_report.user.not_available_message'),
        };
      }

      return {
        success: false,
        error: 'unknown_error',
        message: t('income_report.user.service_unavailable'),
      };
    }
  }

  return {
    currentReport,
    isGenerating,
    isPolling,
    error,
    allowedTypes,
    availableYears,
    generateIncomeReport,
    checkReportStatus,
    startPolling,
    stopPolling,
    fetchAvailableYears,
  };
};
